import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MapPointPicker from '../MapPointPicker';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { PriceInput } from './PriceInput';
import { Section, Field, AddBtn, DelBtn, CoverField, GalleryField, ContactInput, ModuleOrderEditor, PluginTogglesSection, GiftCardsEditor, FaqEditor } from './TemplateFields';
import { orderedSections } from '../../data/businessCatalog';
import { MapPin, Loader2, Phone, Mail, Globe, Facebook, Instagram, CreditCard } from 'lucide-react';

const UNITS = [{ v: 'package', l: 'gab./komplekts' }, { v: 'hour', l: 'stundā' }, { v: 'group', l: 'grupai' }];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessCardEditor({ storeTab, bc, setBc, upload, uploading, pluginOn, togglePlugin, availPlugins }) {
  const [geocoding, setGeocoding] = useState(false);
  const geocodeCard = async () => {
    if (!bc.address) { toast.error('Vispirms ievadi adresi'); return; }
    setGeocoding(true);
    try {
      let res;
      try {
        res = await axios.get(`${API}/geocode`, { params: { q: bc.address } });
      } catch (e1) {
        const stripped = bc.address.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
        if (stripped && stripped !== bc.address) {
          res = await axios.get(`${API}/geocode`, { params: { q: stripped } });
        } else { throw e1; }
      }
      setBc({ lat: res.data.lat, lng: res.data.lng });
      toast.success('Atrasts kartē! Vari precizēt punktu, velkot to.');
    } catch (e) { toast.error('Adrese nav atrasta — pārvieto punktu kartē manuāli'); } finally { setGeocoding(false); }
  };
  return (
          <div className="space-y-8">
            {storeTab === 'identity' && (<>
            <Section title="Pamatinformācija">
              <Field label="Sauklis">
                <Input value={bc.tagline || ''} onChange={(e) => setBc({ tagline: e.target.value })}
                  placeholder="Piem. Tavs vietējais meistars" className="bg-white/5 border-white/10 text-white" data-testid="card-tagline" />
              </Field>
              <Field label="Par mums">
                <Textarea value={bc.about || ''} onChange={(e) => setBc({ about: e.target.value })}
                  placeholder="Pastāsti par savu biznesu, pieredzi, pakalpojumiem..." rows={4} className="bg-white/5 border-white/10 text-white" data-testid="card-about" />
              </Field>
              <CoverField url={bc.cover_url} uploading={uploading} onUpload={(f) => upload(f, (u) => setBc({ cover_url: u }))} testid="card-cover-upload" />
            </Section>
            </>)}

            {storeTab === 'build' && (<>
            <PluginTogglesSection available={availPlugins} plugins={bc.plugins} onToggle={togglePlugin} template="business_card" />

            <ModuleOrderEditor template="business_card" order={orderedSections('business_card', bc.module_order)} onChange={(v) => setBc({ module_order: v })} />
            </>)}

            {storeTab === 'content' && (<>
            <Section title="Pakalpojumi & apmaksa" action={
              <AddBtn onClick={() => setBc({ services: [...(bc.services || []), { id: `new-${Date.now()}`, name: '', price: 0, unit: 'package', duration_min: 60 }] })} testid="add-card-service" />
            }>
              <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 mb-3" data-testid="card-payments-info">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-400/15 flex items-center justify-center shrink-0 mt-0.5"><CreditCard className="w-4 h-4 text-amber-400" /></div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold">Maksājumi caur KoLV · 10% komisija</p>
                    <p className="text-white/50 text-xs mt-1 leading-relaxed">Pievieno pakalpojumu vai preci ar cenu (€0 = bezmaksas pieprasījums). Klients izvēlas datumu/laiku un maksā tev iespējoto veidu. Online apmaksa iet caur KoLV (tu saņem 90% maciņā); klātienes/attālinātā apmaksa rada 10% komisijas parādu, ko nokārto vēlāk.</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-2" data-testid="card-allow-online-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Maksāt online (karte caur KoLV)</p>
                  <p className="text-white/40 text-xs">Klients apmaksā uzreiz ar karti. Tu saņem 90% maciņā.</p>
                </div>
                <Switch checked={bc.allow_online !== false} onCheckedChange={(v) => setBc({ allow_online: v })} data-testid="card-allow-online-switch" />
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-4" data-testid="card-allow-onsite-row">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold">Maksāt klātienē / attālināti</p>
                  <p className="text-white/40 text-xs">Klients apmaksā ārpus KoLV. Vēlāk atzīmē to kā samaksātu — 10% kļūst par parādu.</p>
                </div>
                <Switch checked={!!bc.allow_onsite} onCheckedChange={(v) => setBc({ allow_onsite: v })} data-testid="card-allow-onsite-switch" />
              </div>
              {(bc.services || []).length === 0 && <p className="text-white/40 text-sm">Pievieno pakalpojumu vai preci ar cenu, ko klients var rezervēt un apmaksāt.</p>}
              <div className="space-y-3">
                {(bc.services || []).map((s, i) => (
                  <div key={s.id || i} className="flex items-center gap-2" data-testid={`card-service-row-${i}`}>
                    <Input value={s.name} onChange={(e) => { const a = [...(bc.services || [])]; a[i] = { ...s, name: e.target.value }; setBc({ services: a }); }}
                      placeholder="Nosaukums" className="flex-1 min-w-0 bg-white/5 border-white/10 text-white" data-testid={`card-service-name-${i}`} />
                    <PriceInput value={s.price} emptyValue={0} decimals={2} onChange={(v) => { const a = [...(bc.services || [])]; a[i] = { ...s, price: v }; setBc({ services: a }); }}
                      placeholder="€" className="w-20 shrink-0 bg-white/5 border-white/10 text-white" data-testid={`card-service-price-${i}`} />
                    <select value={s.unit || 'package'} onChange={(e) => { const a = [...(bc.services || [])]; a[i] = { ...s, unit: e.target.value }; setBc({ services: a }); }}
                      className="w-32 shrink-0 h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-2">
                      {UNITS.map((u) => <option key={u.v} value={u.v} className="bg-[#0A0A0A]">{u.l}</option>)}
                    </select>
                    <DelBtn onClick={() => setBc({ services: (bc.services || []).filter((_, j) => j !== i) })} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Kontakti">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ContactInput icon={Phone} value={bc.contacts?.phone} onChange={(v) => setBc({ contacts: { ...bc.contacts, phone: v } })} placeholder="+371 20000000" testid="card-phone" />
                <ContactInput icon={Mail} value={bc.contacts?.email} onChange={(v) => setBc({ contacts: { ...bc.contacts, email: v } })} placeholder="info@bizness.lv" testid="card-email" />
                <ContactInput icon={Globe} value={bc.contacts?.website} onChange={(v) => setBc({ contacts: { ...bc.contacts, website: v } })} placeholder="www.bizness.lv" testid="card-website" />
                <ContactInput icon={Facebook} value={bc.contacts?.facebook} onChange={(v) => setBc({ contacts: { ...bc.contacts, facebook: v } })} placeholder="facebook.com/..." testid="card-facebook" />
                <ContactInput icon={Instagram} value={bc.contacts?.instagram} onChange={(v) => setBc({ contacts: { ...bc.contacts, instagram: v } })} placeholder="instagram.com/..." testid="card-instagram" />
              </div>
            </Section>

            <Section title="Atrašanās vieta & darba laiks">
              <Field label="Adrese">
                <Input value={bc.address || ''} onChange={(e) => setBc({ address: e.target.value })}
                  placeholder="Piem. Brīvības iela 1, Rīga" className="bg-white/5 border-white/10 text-white" data-testid="card-address" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Platums (lat, neobligāti)">
                  <Input type="number" value={bc.lat ?? ''} onChange={(e) => setBc({ lat: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="56.95" className="bg-white/5 border-white/10 text-white" data-testid="card-lat" />
                </Field>
                <Field label="Garums (lng, neobligāti)">
                  <Input type="number" value={bc.lng ?? ''} onChange={(e) => setBc({ lng: e.target.value === '' ? null : parseFloat(e.target.value) })}
                    placeholder="24.11" className="bg-white/5 border-white/10 text-white" data-testid="card-lng" />
                </Field>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-xs font-semibold">Atrašanās vieta kartē</label>
                  <button type="button" onClick={geocodeCard} disabled={geocoding}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 disabled:opacity-50"
                    data-testid="geocode-address-btn">
                    {geocoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />} Atrast adresi kartē
                  </button>
                </div>
                <MapPointPicker lat={bc.lat} lng={bc.lng} onChange={(la, ln) => setBc({ lat: la, lng: ln })} />
                <p className="text-white/40 text-[11px] mt-1.5">Uzspied "Atrast adresi kartē" vai velc punktu, lai precīzi norādītu atrašanās vietu.</p>
              </div>
              <Field label="Darba laiks">
                <Textarea value={bc.working_hours || ''} onChange={(e) => setBc({ working_hours: e.target.value })}
                  placeholder={"P.–Pk. 9:00–18:00\nSestdien 10:00–15:00\nSvētdien slēgts"} rows={3} className="bg-white/5 border-white/10 text-white" data-testid="card-hours" />
              </Field>
            </Section>

            {pluginOn('promotions') && (
            <Section title="Akcijas & piedāvājumi" action={
              <AddBtn onClick={() => setBc({ promotions: [...bc.promotions, { title: '', description: '' }] })} testid="add-promo" />
            }>
              <div className="space-y-3">
                {bc.promotions.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`promo-row-${i}`}>
                    <Input value={p.title} onChange={(e) => { const a = [...bc.promotions]; a[i] = { ...p, title: e.target.value }; setBc({ promotions: a }); }}
                      placeholder="Virsraksts" className="col-span-4 bg-white/5 border-white/10 text-white" />
                    <Input value={p.description || ''} onChange={(e) => { const a = [...bc.promotions]; a[i] = { ...p, description: e.target.value }; setBc({ promotions: a }); }}
                      placeholder="Apraksts" className="col-span-7 bg-white/5 border-white/10 text-white" />
                    <DelBtn onClick={() => setBc({ promotions: bc.promotions.filter((_, j) => j !== i) })} />
                  </div>
                ))}
              </div>
            </Section>
            )}

            {pluginOn('gift_cards') && (
              <GiftCardsEditor data={bc} onChange={setBc} />
            )}

            {pluginOn('gallery') && (
            <GalleryField gallery={bc.gallery} onAdd={(f) => upload(f, (u) => setBc({ gallery: [...bc.gallery, u] }))} onRemove={(i) => setBc({ gallery: bc.gallery.filter((_, j) => j !== i) })} />
            )}

            {pluginOn('faq') && <FaqEditor faq={bc.faq} onChange={(v) => setBc({ faq: v })} />}
            </>)}
          </div>
  );
}
